import type { TileableModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ImageModel } from '@rocco/types/image-model';

/**
 * The AI ImageLabel classification behind a Spaces / Styles tile.
 *
 * Carried for the preview's dev-only debug panel, which is why it is a loose
 * bag of display strings rather than a modelled taxonomy — nothing in the
 * production UI reads it.
 */
export interface ImageLabelSummary {
    /** Source LibraryImage, the id the labelling task is keyed on. */
    libraryImageId: number;
    roomTypePrimary?: string;
    roomTypeSecondary?: string[];
    architecturalStyle?: string[];
    interiorStyle?: string[];
    materials?: string[];
    materialCategories?: string[];
    colorPalette?: string[];
    adminScore?: number;
    aiModel?: string;
}

export interface ProjectTileViewModel {
    id: number;
    title: string;
    link: string;
    images: ImageModel[];
    video: string[];
    hasAwards: boolean;
    badge: string | null;
    viewedCount?: number;
    savedCount?: number;
    /**
     * The item's tracked external website link, or null/absent when it has none.
     * Resolved server-side with the same fallback the detail-page CTA uses (a
     * product's own Website, else the professional's) and already carries the
     * UTM / `_ap_pageid` params. Only populated on surfaces that fetch it.
     */
    website?: string | null;
    professional: {
        id: number;
        name: string;
        link: string;
    };
    canEdit?: boolean;
    bmEditorLink?: string;
    repositionThumbLink?: string;
    /**
     * True when banner data was successfully loaded for this project (the
     * GraphQL banner augment returned an entry). Gates the banner checkbox and
     * lets the mapper drop stale ES slides that no longer exist on the item.
     */
    bannerDataLoaded?: boolean;
    /** Dev-only: the ImageLabel record this tile was built from. */
    imageLabel?: ImageLabelSummary;
}

export const isProjectTileViewModel = (
    model: TileableModel,
): model is ProjectTileViewModel =>
    model && 'hasAwards' in model && 'badge' in model;
